import { create } from 'zustand';

// ===================================================================
// GÓI DỊCH VỤ (bán gói) — port 100% từ app store/CartStore.js
// Giữ nguyên toàn bộ logic dịch vụ cũ bên dưới, chỉ THÊM phần package.
// ===================================================================

/** Giá thực bán 1 thẻ: customPrice > templateSalePrice > templatePrice. */
export const resolvePackagePrice = (item) => {
    if (!item) return 0;
    if (item.customPrice != null && item.customPrice !== '') return Number(item.customPrice) || 0;
    if (item.templateSalePrice != null) return Number(item.templateSalePrice) || 0;
    return Number(item.templatePrice) || 0;
};

/** Số buổi: customSessions ?? templateSessions. */
export const resolvePackageSessions = (item) => {
    if (!item) return 0;
    if (item.customSessions != null && item.customSessions !== '') return Number(item.customSessions) || 0;
    return Number(item.templateSessions) || 0;
};

/** Hoa hồng NV bán: floor(price * percent / 100). */
export const resolvePackageSellerCommission = (item) => {
    const price = resolvePackagePrice(item);
    const percent = Number(item?.sellerCommissionPercent || 0);
    return Math.floor((price * percent) / 100);
};

/** package cart entry → CheckoutItemDto kind='package' (strip null/empty). */
export const toPackageCheckoutItemDto = (item) => {
    const dto = {
        kind: 'package',
        packageId: Number(item.packageId),
        sellerEmployeeId: Number(item.sellerEmployeeId),
        quantity: Math.max(1, Number(item.quantity) || 1),
    };
    if (item.customPrice != null && item.customPrice !== '') dto.customPrice = Number(item.customPrice);
    if (item.customSessions != null && item.customSessions !== '') dto.customSessions = Number(item.customSessions);
    if (item.customValidityMonths != null && item.customValidityMonths !== '') {
        dto.customValidityMonths = Number(item.customValidityMonths);
    }
    if (item.overrideReason?.trim()) dto.overrideReason = item.overrideReason.trim();
    if (item.note?.trim()) dto.note = item.note.trim();
    if (item.internalNote?.trim()) dto.internalNote = item.internalNote.trim();
    return dto;
};

/** 1 row billData.details (dịch vụ/sản phẩm/combo) → CheckoutItemDto kind='service'. */
export const toServiceCheckoutItemDto = (detail) => ({
    kind: 'service',
    serviceId: Number(detail.serviceId),
    variantId: detail.variantId != null ? Number(detail.variantId) : undefined,
    serviceQuantity: Number(detail.quantity || 1),
    employeeId: Number(detail.employeeId),
    price: Number(detail.price || 0),
});

/** Validate giỏ gói trước khi gửi /bills/checkout. Trả về mảng error (rỗng = OK). */
export const validatePackageCart = (cart) => {
    const errors = [];
    (cart || []).forEach((pkg) => {
        const label = `Gói "${pkg.name}"`;
        if (!pkg.sellerEmployeeId) {
            errors.push(`${label}: chưa chọn NV bán`);
        }
        const hasOverride =
            (pkg.customPrice != null && pkg.customPrice !== '') ||
            (pkg.customSessions != null && pkg.customSessions !== '') ||
            (pkg.customValidityMonths != null && pkg.customValidityMonths !== '');
        if (hasOverride) {
            const reason = (pkg.overrideReason || '').trim();
            if (reason.length < 5) {
                errors.push(`${label}: cần lý do điều chỉnh ≥ 5 ký tự`);
            }
        }
        const qty = Number(pkg.quantity) || 0;
        if (qty < 1) {
            errors.push(`${label}: số lượng không hợp lệ`);
        }
    });
    return errors;
};

/**
 * session cart entry → CheckoutItemDto kind='package_session'.
 * sessionPerformers: mỗi buổi 1 thợ; length phải bằng sessionCount (BE tính hoa hồng thợ làm).
 */
export const toSessionCheckoutItemDto = (item) => {
    const sessionCount = Math.max(1, Number(item.sessionCount) || 1);
    const performers = (item.performers || []).slice(0, sessionCount).map((id) => Number(id));
    return {
        kind: 'package_session',
        customerPackageId: Number(item.customerPackageId),
        sessionCount,
        sessionPerformers: performers,
    };
};

/**
 * session cart entry → SessionEditItemDto cho PUT /bills/:id (sửa đơn).
 * Khác toSessionCheckoutItemDto: KHÔNG có field `kind`.
 */
export const toSessionEditItemDto = (item) => {
    const sessionCount = Math.max(1, Number(item.sessionCount) || 1);
    const performers = (item.performers || []).slice(0, sessionCount).map((id) => Number(id));
    return {
        customerPackageId: Number(item.customerPackageId),
        sessionCount,
        sessionPerformers: performers,
    };
};

/** Validate session cart: mỗi item đủ thợ cho từng buổi. Trả về mảng error (rỗng = OK). */
export const validateSessionCart = (sessionCart) => {
    const errors = [];
    (sessionCart || []).forEach((item, idx) => {
        const label = item.code || item.packageName || `Thẻ #${idx + 1}`;
        const sessionCount = Math.max(1, Number(item.sessionCount) || 1);
        const performers = item.performers || [];
        if (performers.length !== sessionCount) {
            errors.push(`${label}: cần chọn đủ ${sessionCount} NV thực hiện (đang có ${performers.length})`);
            return;
        }
        const missing = performers.findIndex((p) => !p || Number(p) <= 0);
        if (missing >= 0) {
            errors.push(`${label}: thiếu NV thực hiện buổi #${missing + 1}`);
        }
    });
    return errors;
};

/**
 * Build POST body cho /bills/checkout — hỗ trợ mixed cart (dịch vụ + gói + dùng buổi).
 * billData = object đã build ở component (giống payload /bills/create cũ, có .details).
 */
export const buildCheckoutPayload = (billData, packageCart, sessionCart) => {
    const serviceItems = (billData?.details || []).map(toServiceCheckoutItemDto);
    const packageItems = (packageCart || []).map(toPackageCheckoutItemDto);
    const sessionItems = (sessionCart || []).map(toSessionCheckoutItemDto);

    return {
        customerId: Number(billData.customerId),
        employeeId: billData.employeeId != null ? Number(billData.employeeId) : undefined,
        items: [...serviceItems, ...packageItems, ...sessionItems],
        originalTotal: Number(billData.originalTotal ?? billData.total ?? 0),
        total: Number(billData.total ?? 0),
        discount: Number(billData.discount ?? 0),
        discountType: billData.discountType ?? 'VND',
        pointUsed: Number(billData.pointUsed ?? 0),
        pointReceived: Number(billData.pointReceived ?? 0),
        owe: Number(billData.owe ?? 0),
        status: billData.status ?? 'paid',
        paymentMethod: billData.paymentMethod ?? 'Tiền mặt',
        note: billData.note?.trim?.() || billData.note || undefined,
        internalNote: billData.internalNote?.trim?.() || billData.internalNote || undefined,
    };
};

/** Idempotency-Key sinh tại FE, giữ 1 key/lần submit để retry an toàn. */
export const generateIdempotencyKey = () =>
    `chk_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

const computePackageTotal = (cart) =>
    (cart || []).reduce(
        (sum, it) => sum + (resolvePackagePrice(it) || 0) * (Number(it.quantity) || 1),
        0,
    );

const generatePackageUid = () => `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const useCartStore = create((set, get) => ({
    cartByCustomer: {},
    totalAmount: {},

    // ===== Gói dịch vụ (bán gói) =====
    packageCartByCustomer: {},
    packageTotalAmount: {},

    addPackageItem: (customerId, input) =>
        set((state) => {
            const customerCart = state.packageCartByCustomer[customerId] || [];
            const existing = customerCart.find((it) => it.packageId === input.packageId);

            let updatedCart;
            if (existing) {
                updatedCart = customerCart.map((it) =>
                    it.packageId === input.packageId
                        ? { ...it, quantity: (Number(it.quantity) || 0) + 1 }
                        : it,
                );
            } else {
                const newItem = {
                    _uid: generatePackageUid(),
                    packageId: input.packageId,
                    name: input.name,
                    urlImg: input.urlImg ?? null,
                    code: input.code ?? null,
                    templatePrice: Number(input.templatePrice ?? input.price ?? 0),
                    templateSalePrice:
                        input.templateSalePrice != null ? Number(input.templateSalePrice) : null,
                    templateSessions: Number(input.templateSessions ?? input.totalSessions ?? 1),
                    templateValidityMonths: input.templateValidityMonths ?? input.validityMonths ?? null,
                    sellerCommissionPercent: Number(input.sellerCommissionPercent ?? 0),
                    performerCommissionPercent: Number(input.performerCommissionPercent ?? 0),
                    sellerEmployeeId: input.sellerEmployeeId ?? null,
                    sellerName: input.sellerName ?? null,
                    quantity: 1,
                    customPrice: null,
                    customSessions: null,
                    customValidityMonths: null,
                    overrideReason: null,
                    note: null,
                    internalNote: null,
                };
                updatedCart = [...customerCart, newItem];
            }

            return {
                packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: updatedCart },
                packageTotalAmount: {
                    ...state.packageTotalAmount,
                    [customerId]: computePackageTotal(updatedCart),
                },
            };
        }),

    removeOnePackage: (customerId, packageId) =>
        set((state) => {
            const customerCart = state.packageCartByCustomer[customerId] || [];
            const updatedCart = customerCart
                .map((it) =>
                    it.packageId === packageId
                        ? { ...it, quantity: (Number(it.quantity) || 0) - 1 }
                        : it,
                )
                .filter((it) => Number(it.quantity) > 0);

            return {
                packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: updatedCart },
                packageTotalAmount: {
                    ...state.packageTotalAmount,
                    [customerId]: computePackageTotal(updatedCart),
                },
            };
        }),

    updatePackageQuantity: (customerId, packageId, newQuantity) =>
        set((state) => {
            const customerCart = state.packageCartByCustomer[customerId] || [];
            const qty = parseInt(newQuantity, 10);

            let updatedCart;
            if (isNaN(qty) || qty <= 0) {
                updatedCart = customerCart.filter((it) => it.packageId !== packageId);
            } else {
                updatedCart = customerCart.map((it) =>
                    it.packageId === packageId ? { ...it, quantity: qty } : it,
                );
            }

            return {
                packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: updatedCart },
                packageTotalAmount: {
                    ...state.packageTotalAmount,
                    [customerId]: computePackageTotal(updatedCart),
                },
            };
        }),

    updatePackageItem: (customerId, uid, overrides) =>
        set((state) => {
            const customerCart = state.packageCartByCustomer[customerId] || [];
            const updatedCart = customerCart.map((it) =>
                it._uid === uid ? { ...it, ...overrides } : it,
            );
            return {
                packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: updatedCart },
                packageTotalAmount: {
                    ...state.packageTotalAmount,
                    [customerId]: computePackageTotal(updatedCart),
                },
            };
        }),

    removePackageItem: (customerId, uid) =>
        set((state) => {
            const customerCart = state.packageCartByCustomer[customerId] || [];
            const updatedCart = customerCart.filter((it) => it._uid !== uid);
            return {
                packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: updatedCart },
                packageTotalAmount: {
                    ...state.packageTotalAmount,
                    [customerId]: computePackageTotal(updatedCart),
                },
            };
        }),

    removeAllPackageFromCart: (customerId) =>
        set((state) => ({
            packageCartByCustomer: { ...state.packageCartByCustomer, [customerId]: [] },
            packageTotalAmount: { ...state.packageTotalAmount, [customerId]: 0 },
        })),

    getPackageCart: (customerId) => get().packageCartByCustomer[customerId] || [],
    getPackageTotal: (customerId) => get().packageTotalAmount[customerId] || 0,
    getPackageItemCount: (customerId) =>
        (get().packageCartByCustomer[customerId] || []).reduce(
            (sum, it) => sum + (Number(it.quantity) || 0),
            0,
        ),

    // ===== Dùng buổi gói (session cart) — port store/CartStore.js =====
    // Item: { _uid, customerPackageId, code, packageName, amountPerSession, remainingSessions,
    //         sessionCount, performers: [employeeId|null, ...] }. Tiền đóng góp bill = 0.
    packageSessionCartByCustomer: {},

    addSessionItem: (customerId, input) =>
        set((state) => {
            const cart = state.packageSessionCartByCustomer[customerId] || [];
            const newCount = Math.max(1, Number(input.sessionCount) || 1);
            const defaultPerformerId =
                input.defaultPerformerId != null ? Number(input.defaultPerformerId) : null;
            const resizePerformers = (oldPerformers, len) => {
                const out = (oldPerformers || []).slice(0, len);
                while (out.length < len) out.push(defaultPerformerId);
                return out;
            };
            const existing = cart.find((it) => it.customerPackageId === input.customerPackageId);
            let updatedCart;
            if (existing) {
                updatedCart = cart.map((it) =>
                    it.customerPackageId === input.customerPackageId
                        ? {
                              ...it,
                              sessionCount: newCount,
                              performers: resizePerformers(it.performers, newCount),
                          }
                        : it,
                );
            } else {
                updatedCart = [
                    ...cart,
                    {
                        _uid: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                        customerPackageId: input.customerPackageId,
                        code: input.code,
                        packageName: input.packageName,
                        amountPerSession: Number(input.amountPerSession || 0),
                        remainingSessions: Number(input.remainingSessions || 0),
                        sessionCount: newCount,
                        performers: resizePerformers([], newCount),
                    },
                ];
            }
            return {
                packageSessionCartByCustomer: {
                    ...state.packageSessionCartByCustomer,
                    [customerId]: updatedCart,
                },
            };
        }),

    updateSessionCount: (customerId, uid, count) =>
        set((state) => {
            const cart = state.packageSessionCartByCustomer[customerId] || [];
            const qty = Math.max(1, parseInt(count, 10) || 1);
            return {
                packageSessionCartByCustomer: {
                    ...state.packageSessionCartByCustomer,
                    [customerId]: cart.map((it) => {
                        if (it._uid !== uid) return it;
                        const performers = (it.performers || []).slice(0, qty);
                        while (performers.length < qty) performers.push(null);
                        return { ...it, sessionCount: qty, performers };
                    }),
                },
            };
        }),

    setSessionPerformer: (customerId, uid, sessionIdx, performerId) =>
        set((state) => {
            const cart = state.packageSessionCartByCustomer[customerId] || [];
            return {
                packageSessionCartByCustomer: {
                    ...state.packageSessionCartByCustomer,
                    [customerId]: cart.map((it) => {
                        if (it._uid !== uid) return it;
                        const performers = [...(it.performers || [])];
                        while (performers.length <= sessionIdx) performers.push(null);
                        performers[sessionIdx] = performerId != null ? Number(performerId) : null;
                        return { ...it, performers };
                    }),
                },
            };
        }),

    removeSessionItem: (customerId, uid) =>
        set((state) => {
            const cart = state.packageSessionCartByCustomer[customerId] || [];
            return {
                packageSessionCartByCustomer: {
                    ...state.packageSessionCartByCustomer,
                    [customerId]: cart.filter((it) => it._uid !== uid),
                },
            };
        }),

    removeAllSessionItems: (customerId) =>
        set((state) => ({
            packageSessionCartByCustomer: {
                ...state.packageSessionCartByCustomer,
                [customerId]: [],
            },
        })),

    // Thay toàn bộ session cart cho 1 khách — dùng prefill khi mở sửa đơn (port app).
    replaceSessionCart: (customerId, items) =>
        set((state) => ({
            packageSessionCartByCustomer: {
                ...state.packageSessionCartByCustomer,
                [customerId]: (items || []).map((it) => ({
                    _uid: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    customerPackageId: Number(it.customerPackageId),
                    code: it.code || '',
                    packageName: it.packageName || '',
                    amountPerSession: Number(it.amountPerSession || 0),
                    remainingSessions: Number(it.remainingSessions || 0),
                    sessionCount: Math.max(1, Number(it.sessionCount) || 1),
                    performers: (it.performers || []).map((p) => (p != null ? Number(p) : null)),
                })),
            },
        })),

    getSessionCart: (customerId) => get().packageSessionCartByCustomer[customerId] || [],

    addToCart: (customerId, product) =>
        set((state) => {
            const price = Math.floor(product.price || 0);
            const customerCart = state.cartByCustomer[customerId] || [];
            const productKey = product.selectedVariant
                ? `${product.id}-${product.selectedVariant.id}`
                : String(product.id);

            const existingItem = customerCart.find((item) => {
                if (product.selectedVariant) {
                    return item.id === product.id && item.selectedVariant?.id === product.selectedVariant.id;
                }
                return item.id === product.id;
            });

            const addQuantity = product.quantity ? parseFloat(product.quantity) : 1;
            let updatedCart;

            if (existingItem) {
                updatedCart = customerCart.map((item) => {
                    if (product.selectedVariant) {
                        if (item.id === product.id && item.selectedVariant?.id === product.selectedVariant.id) {
                            return { ...item, quantity: parseFloat((parseFloat(item.quantity) + addQuantity).toFixed(10)) };
                        }
                        return item;
                    }
                    if (item.id === product.id) {
                        return { ...item, quantity: parseFloat((parseFloat(item.quantity) + addQuantity).toFixed(10)) };
                    }
                    return item;
                });
            } else {
                updatedCart = [
                    ...customerCart,
                    { ...product, quantity: addQuantity, price, supportEmployees: [], productKey },
                ];
            }

            return {
                cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart },
                totalAmount: {
                    ...state.totalAmount,
                    [customerId]: updatedCart.reduce((s, it) => s + parseFloat(it.price) * parseFloat(it.quantity), 0),
                },
            };
        }),

    removeFromCart: (customerId, productId, variantId = null) =>
        set((state) => {
            const customerCart = state.cartByCustomer[customerId] || [];
            const updatedCart = customerCart
                .map((item) => {
                    if (variantId) {
                        if (item.id === productId && item.selectedVariant?.id === variantId) {
                            return { ...item, quantity: parseFloat((parseFloat(item.quantity) - 1).toFixed(10)) };
                        }
                        return item;
                    }
                    if (item.id === productId) {
                        return { ...item, quantity: parseFloat((parseFloat(item.quantity) - 1).toFixed(10)) };
                    }
                    return item;
                })
                .filter((item) => parseFloat(item.quantity) > 0);

            return {
                cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart },
                totalAmount: {
                    ...state.totalAmount,
                    [customerId]: updatedCart.reduce((s, it) => s + parseFloat(it.price) * parseFloat(it.quantity), 0),
                },
            };
        }),

    updateQuantity: (customerId, productId, variantId, newQuantity) =>
        set((state) => {
            const customerCart = state.cartByCustomer[customerId] || [];
            const quantity = parseFloat(parseFloat(newQuantity).toFixed(10));

            if (isNaN(quantity) || quantity <= 0) {
                const updatedCart = customerCart.filter((item) => {
                    if (variantId) return !(item.id === productId && item.selectedVariant?.id === variantId);
                    return item.id !== productId;
                });
                return {
                    cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart },
                    totalAmount: {
                        ...state.totalAmount,
                        [customerId]: updatedCart.reduce((s, it) => s + parseFloat(it.price) * parseFloat(it.quantity), 0),
                    },
                };
            }

            const updatedCart = customerCart.map((item) => {
                if (variantId) {
                    if (item.id === productId && item.selectedVariant?.id === variantId) return { ...item, quantity };
                    return item;
                }
                if (item.id === productId) return { ...item, quantity };
                return item;
            });

            return {
                cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart },
                totalAmount: {
                    ...state.totalAmount,
                    [customerId]: updatedCart.reduce((s, it) => s + parseFloat(it.price) * parseFloat(it.quantity), 0),
                },
            };
        }),

    removeAllFromCart: (customerId) =>
        set((state) => ({
            cartByCustomer: { ...state.cartByCustomer, [customerId]: [] },
            totalAmount: { ...state.totalAmount, [customerId]: 0 },
        })),

    replaceCart: (customerId, items) =>
        set((state) => ({
            cartByCustomer: { ...state.cartByCustomer, [customerId]: items },
            totalAmount: {
                ...state.totalAmount,
                [customerId]: items.reduce((s, it) => s + parseFloat(it.price) * parseFloat(it.quantity), 0),
            },
        })),

    transferCartBetweenCustomers: (fromId, toId) =>
        set((state) => {
            if (!fromId || !toId || fromId === toId) return state;
            const fromCart = state.cartByCustomer[fromId] || [];
            const toCart = state.cartByCustomer[toId] || [];
            const mergedMap = new Map();
            const buildKey = (it) => `${it.id}-${it.selectedVariant?.id || 'null'}`;
            toCart.forEach((it) => mergedMap.set(buildKey(it), { ...it }));
            fromCart.forEach((it) => {
                const k = buildKey(it);
                const ex = mergedMap.get(k);
                if (ex) {
                    mergedMap.set(k, { ...ex, quantity: parseFloat(((parseFloat(ex.quantity) || 0) + (parseFloat(it.quantity) || 0)).toFixed(10)) });
                } else {
                    mergedMap.set(k, { ...it });
                }
            });
            const merged = Array.from(mergedMap.values());
            return {
                cartByCustomer: { ...state.cartByCustomer, [toId]: merged, [fromId]: [] },
                totalAmount: {
                    ...state.totalAmount,
                    [toId]: merged.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0), 0),
                    [fromId]: 0,
                },
            };
        }),

    setSupportEmployees: (customerId, productId, supportEmployees, variantId = null) =>
        set((state) => {
            const customerCart = state.cartByCustomer[customerId] || [];
            const updatedCart = customerCart.map((item) => {
                if (item.id !== productId) return item;
                if (variantId !== null && variantId !== undefined) {
                    return item.selectedVariant?.id === variantId ? { ...item, supportEmployees } : item;
                }
                return item.selectedVariant ? item : { ...item, supportEmployees };
            });
            return { cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart } };
        }),

    setSupportEmployeesForAll: (customerId, supportEmployees) =>
        set((state) => {
            const customerCart = state.cartByCustomer[customerId] || [];
            const updatedCart = customerCart.map((item) => ({ ...item, supportEmployees }));
            return { cartByCustomer: { ...state.cartByCustomer, [customerId]: updatedCart } };
        }),

    calculateTotal: (customerId) => {
        const cart = get().cartByCustomer[customerId] || [];
        const amount = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
        set((state) => ({
            totalAmount: { ...state.totalAmount, [customerId]: amount },
        }));
    },

    totalItems: (customerId) => {
        const cart = get().cartByCustomer[customerId] || [];
        return cart.reduce((total, item) => total + item.quantity, 0);
    },

    getCart: (customerId) => get().cartByCustomer[customerId] || [],
    getTotal: (customerId) => get().totalAmount[customerId] || 0,
}));

export default useCartStore;
